import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import {
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { type JSX, useCallback, useMemo, useState } from 'react'
import { Table } from '~/features/table/ui/table'
import { useTRPC } from '~/services/trpc'
import { SettingsActions, SettingsContent, useSettingTitle } from '~/templates/settings'

import { Button, Search } from '@hap/components'
import { getRandomBrandColor } from '@hap/utils'

import { PLACEHOLDER_DATA, colorOptions, columns } from './-components/table-config'

export const Route = createFileRoute('/_authenticated/$alias/settings/flags/')({
  component: FlagsSettings,
})

function FlagsSettings(): JSX.Element {
  const trpc = useTRPC()
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const query = ((location.search as Record<string, unknown>).query as string) || ''

  const setQuery = useCallback(
    (value: string | undefined) => {
      navigate({
        to: location.pathname,
        search: {
          ...(location.search as Record<string, unknown>),
          query: value,
          page: 1,
        },
        replace: true,
      })
    },
    [location.pathname, location.search, navigate],
  )

  const [newFlag, setNewFlag] = useState<
    | false
    | {
        id: string
        name: string
        color: string
        usage: number
        editing: boolean
        onBlur: () => void
      }
  >(false)

  const { data, isLoading } = useQuery(trpc.flags.list.queryOptions())

  const [sort, setSort] = useState<SortingState>([{ id: 'name', desc: false }])

  useSettingTitle([
    {
      label: 'Flags',
      description:
        'Flags help you mark important moments in customer interactions for quick reference and follow-up.',
    },
  ])

  const flags = useMemo(() => {
    const flags = []
    if (newFlag) flags.push(newFlag)
    if (data) {
      if (!query) flags.push(...data)
      else
        flags.push(
          ...data.filter((flag) => flag.name.toLowerCase().includes(query.toLowerCase())),
        )
    } else {
      flags.push(...PLACEHOLDER_DATA)
    }
    return flags
  }, [data, query, newFlag])

  const onBlur = useCallback(() => {
    setNewFlag(false)
  }, [])

  const table = useReactTable({
    columns: columns,
    data: flags,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    onSortingChange: setSort,
    state: {
      sorting: sort,
    },
  })

  return (
    <>
      <SettingsActions>
        <Button
          onClick={() =>
            setNewFlag({
              id: '',
              name: ' Flag name',
              usage: 0,
              color: getRandomBrandColor(colorOptions),
              editing: true,
              onBlur,
            })
          }
        >
          New Flag
        </Button>
      </SettingsActions>
      <SettingsContent spacing="sm">
        <div className="flex justify-between gap-3">
          <Search
            placeholder="Search by name"
            onSearch={(value) => setQuery(value || undefined)}
            className="flex w-125 max-w-md shrink-0 grow"
            defaultValue={query}
          />
        </div>
        <Table table={table} isLoading={isLoading} />
      </SettingsContent>
    </>
  )
}
